import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';

import Modal from '../Modal';
import Button from '../Button';

const WarningMissingItemsModal = ({
  isOpen,
  className,
  onCloseModal,
}) => {
  const { t } = useTranslation();

  const { currentSystem } = useSelector(({ systems }) => systems);

  const {
    specification: { missingItems = [] },
    frameSpecification: { missingItems: frameMissingItems = [] },
  } = useSelector(({ order }) => order);

  const transformItemName = (str) => str.split(' ').join('-');

  const filteredMissingItems = missingItems.reduce((acc, item) => {
    if (!acc.some((a) => a.articleCode === item.articleCode)) {
      acc.push({ ...item, item: transformItemName(item.item) });
    }
    return acc;
  }, []);

  const filteredFrameMissingItems = frameMissingItems.reduce((acc, item) => {
    if (!acc.some((a) => a.articleCode === item.articleCode)) {
      acc.push({ ...item, item: transformItemName(item.item) });
    }
    return acc;
  }, []);

  const renderFrameMissingItems = () => {
    const groupedMissingItems = filteredFrameMissingItems.reduce((acc, m) => {
      const { item, articleCode, frameNumber, groupName } = m;
      const key = `${frameNumber}${groupName}`;
      const group = acc.find((a) => key in a);

      if (group) {
        group[key].push({ item, articleCode });
      } else {
        acc.push({
          [key]: [{ item, articleCode }],
        });
      }

      return acc;
    }, []);

    return (
      <div>
        {groupedMissingItems.map((items) => {
          const [groupName, itemList] = Object.entries(items).flat();

          return (
            <>
              <span>
                {`— ${t('warningMissingItemsModal.group')}`}
                <b>{`"${groupName.slice(1)}":`}</b>
              </span>
              <ul className="list">
                {itemList.map(({ item, articleCode }) => (
                  articleCode
                    ? (
                      <li key={articleCode}>
                        {`${t(`warningMissingItemsModal.${item}`)} ("${articleCode}")`}
                      </li>
                    )
                    : (
                      <li key={articleCode}>
                        &ndash;&nbsp;&nbsp;
                        {item}
                      </li>
                    )))}
              </ul>
            </>
          );
        })}
      </div>
    );
  };

  const renderMissingItems = () => (
    <ul className="list">
      {filteredMissingItems.map(({ item, articleCode }) => (
        <li key={articleCode}>
          {`${t(`warningMissingItemsModal.${item}`)} ("${articleCode}")`}
        </li>
      ))}
    </ul>
  );


  if (!isOpen) return null;


  return (
    <Modal
      opened={isOpen}
      closeModal={onCloseModal}
      className={className}
      shouldDisableBodyScroll
    >
      <div className="modal-order-icon-wrapper">
        <ReactSVG
          wrapper="div"
          className="modal-order-icon"
          src="/src/client/assets/icons/price-package-icon.svg"
        />
      </div>
      <h2 className="headings-h2">
        {t(`warningMissingItemsModal.${frameMissingItems.length > 1 || missingItems.length > 1
          ? 'missing-items'
          : 'missing-item'
          }`)}
      </h2>

      <div className="content">
        {currentSystem === 'frame-facades'
          ? renderFrameMissingItems()
          : renderMissingItems()}
      </div>

      <div className="action-buttons">
        <div className="action-buttons-inner">
          <Button
            value={t('warningMissingItemsModal.return')}
            type="rounded"
            onClick={onCloseModal}
            isDisabled={false}
          />
        </div>
      </div>
    </Modal>
  );
};

WarningMissingItemsModal.defaultProps = {
  isOpen: false,
  className: 'action-modal missing-items',
};

WarningMissingItemsModal.propTypes = {
  isOpen: PropTypes.bool,
  className: PropTypes.string,
  onCloseModal: PropTypes.func.isRequired,
};

export default WarningMissingItemsModal;
